import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon, Sparkles, Users, PartyPopper } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const TIMES = [
  "5:00 PM", "5:30 PM", "6:00 PM", "6:30 PM", "7:00 PM",
  "7:30 PM", "8:00 PM", "8:30 PM", "9:00 PM", "9:30 PM",
];

const TYPES = [
  { id: "table", label: "Table / RSVP", sub: "Any party size", icon: Users },
  { id: "group", label: "Group Party", sub: "Birthday / large crew", icon: PartyPopper },
  { id: "vip", label: "VIP Room", sub: "Private lounge", icon: Sparkles },
];

export default function Reservations() {
  const [bookingType, setBookingType] = useState("table");
  const [date, setDate] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    party_size: "4",
    time: "",
    occasion: "",
    notes: "",
    group_name: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    if (!date) {
      toast.error("Pick a date for your reservation");
      return;
    }
    if (bookingType !== "group" && !form.time) {
      toast.error("Pick a time");
      return;
    }
    setSubmitting(true);
    try {
      const { getReferralCode } = await import("@/lib/referral");
      const { data } = await api.post("/reservations", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        party_size: parseInt(form.party_size, 10) || 1,
        date: format(date, "yyyy-MM-dd"),
        time: form.time || null,
        occasion: form.occasion || null,
        notes: form.notes || null,
        booking_type: bookingType,
        group_name: bookingType === "group" ? form.group_name || null : null,
        referrer_code: getReferralCode() || null,
      });
      const msg =
        bookingType === "vip"
          ? "VIP room request received — we'll confirm by phone."
          : bookingType === "group"
          ? "Group RSVP received — we'll call to lock in details."
          : "Reservation request received — see you soon!";
      toast.success(msg);
      setForm({
        name: "",
        email: "",
        phone: "",
        party_size: "4",
        time: "",
        occasion: "",
        notes: "",
        group_name: "",
      });
      setDate(null);
      navigate(`/rsvp/${data.id}?new=1`);
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) ||
          "Something went wrong. Please call us at the number below."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      id="reservations"
      data-testid="reservations-section"
      className="trio-section py-24 lg:py-32 border-b border-[var(--trio-border)]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold mb-4">
              Reserve · RSVP · VIP
            </div>
            <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.9] mb-6">
              Hold a<br /><span className="text-[var(--trio-primary)]">Seat.</span>
            </h2>
            <p className="text-base lg:text-lg text-[var(--trio-muted)] leading-relaxed max-w-md">
              Regular tables, large-party RSVPs, or our private VIP lounge —
              pick a vibe below and tell us when.
            </p>

            <div
              data-testid="booking-type-toggle"
              className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-md"
            >
              {TYPES.map((t) => {
                const Icon = t.icon;
                const active = bookingType === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setBookingType(t.id)}
                    data-testid={`booking-type-${t.id}`}
                    className={`p-4 text-left border transition-all ${
                      active
                        ? "border-[var(--trio-primary)] bg-[var(--trio-primary)] text-white"
                        : "border-[var(--trio-border)] hover:border-[var(--trio-text)]"
                    }`}
                  >
                    <Icon className="w-5 h-5 mb-3" />
                    <div className="font-display uppercase text-base leading-tight tracking-tight">
                      {t.label}
                    </div>
                    <div className="text-[10px] opacity-70 mt-1">{t.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="reservation-form"
            className="lg:col-span-7 bg-[var(--trio-surface)] border border-[var(--trio-border)] p-8 lg:p-10 space-y-6"
          >
            {bookingType === "group" && (
              <div>
                <Label htmlFor="r-group" className="text-xs tracking-widest uppercase">
                  Group / Celebrant Name
                </Label>
                <Input
                  id="r-group"
                  data-testid="res-group-name"
                  placeholder="e.g. Jasmine's 30th"
                  value={form.group_name}
                  onChange={(e) => update("group_name", e.target.value)}
                  className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
                />
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="r-name" className="text-xs tracking-widest uppercase">
                  Your Full Name
                </Label>
                <Input
                  id="r-name"
                  required
                  data-testid="res-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
                />
              </div>
              <div>
                <Label htmlFor="r-phone" className="text-xs tracking-widest uppercase">
                  Phone
                </Label>
                <Input
                  id="r-phone"
                  required
                  data-testid="res-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="So we can call or text to confirm"
                  className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="r-email" className="text-xs tracking-widest uppercase">
                  Email
                </Label>
                <Input
                  id="r-email"
                  type="email"
                  required
                  data-testid="res-email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@email.com — we'll send your confirmation here"
                  className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
                />
              </div>
              <div>
                <Label htmlFor="r-party" className="text-xs tracking-widest uppercase">
                  Party Size
                </Label>
                <Input
                  id="r-party"
                  type="number"
                  min="1"
                  required
                  data-testid="res-party-size"
                  value={form.party_size}
                  onChange={(e) => update("party_size", e.target.value)}
                  className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
                />
              </div>

              <div>
                <Label className="text-xs tracking-widest uppercase">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="res-date-trigger"
                      className="mt-2 w-full flex items-center justify-between bg-[var(--trio-bg)] border border-[var(--trio-border)] px-3 py-2 text-sm hover:border-[var(--trio-text)]"
                    >
                      {date ? format(date, "EEE, MMM d, yyyy") : "Choose a date"}
                      <CalendarIcon className="w-4 h-4 opacity-60" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="p-0 bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none"
                    align="start"
                  >
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {bookingType !== "group" && (
                <div>
                  <Label className="text-xs tracking-widest uppercase">Time</Label>
                  <Select value={form.time} onValueChange={(v) => update("time", v)}>
                    <SelectTrigger
                      data-testid="res-time"
                      className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)]"
                    >
                      <SelectValue placeholder="Pick a time" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none">
                      {TIMES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="r-occ" className="text-xs tracking-widest uppercase">
                Occasion (optional)
              </Label>
              <Input
                id="r-occ"
                data-testid="res-occasion"
                placeholder="Birthday, anniversary, date night..."
                value={form.occasion}
                onChange={(e) => update("occasion", e.target.value)}
                className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
              />
            </div>
            <div>
              <Label htmlFor="r-notes" className="text-xs tracking-widest uppercase">
                Notes / Special Requests
              </Label>
              <Textarea
                id="r-notes"
                data-testid="res-notes"
                rows={4}
                placeholder="Allergies, decorations, surprises, anything else we should know."
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="res-submit"
              className="w-full sm:w-auto bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-8 py-4 text-sm tracking-widest uppercase font-bold disabled:opacity-60 transition-colors"
            >
              {submitting
                ? "Sending..."
                : bookingType === "vip"
                ? "Request VIP Room"
                : bookingType === "group"
                ? "Send Group RSVP"
                : "Request Reservation"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
